import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import {
  CheckRounded,
  ContentCopyRounded,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputAdornment,
  Link,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ShareSubmissionRequest,
  postSubmissionShare,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";

import { validateEmail } from "@/common/utils";
import { useDevEnvironment } from "@/contexts/DevContext";
import { useServiceConfigurations } from "@/hooks/useCommunities";

import styles from "./ShareReportContent.module.scss";

interface ShareReportContentProps {
  appName: string;
  orgId: string;
  submissionId: string;
  appTitle?: string;
  framed?: boolean;
  onShared?: () => void;
}

const ShareReportContent = ({
  appName,
  orgId,
  submissionId,
  appTitle = "Report",
  framed = true,
  onShared,
}: ShareReportContentProps) => {
  const serviceConfigurations = useServiceConfigurations();
  const { isDevEnv: isDevMode } = useDevEnvironment();
  const disabledTransferTextColor = !isDevMode
    ? {
        color: (theme: { palette: { text: { disabled: string } } }) =>
          `${theme.palette.text.disabled} !important`,
      }
    : undefined;
  const [shareEmail, setShareEmail] = useState("");
  const [hasInteractedWithEmail, setHasInteractedWithEmail] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [selectedTransferAgency, setSelectedTransferAgency] = useState("");
  const [linkJustCopied, setLinkJustCopied] = useState(false);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const transferAgencyOptions = useMemo(() => {
    return Object.values(serviceConfigurations)
      .filter((cfg) => cfg.id)
      .map((cfg) => ({
        id: isDevMode ? cfg.testID : cfg.id,
        name: cfg.fullName ?? cfg.shortName ?? "Unknown",
      }))
      .filter((o) => o.id.length > 0 && o.id !== orgId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [isDevMode, orgId, serviceConfigurations]);

  useEffect(() => {
    setSelectedTransferAgency("");
  }, [orgId]);

  const shareLink = useMemo(
    () =>
      `${window.location.origin}/${appName}/${orgId}/report/${submissionId}/condensed`,
    [appName, orgId, submissionId],
  );

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
      setLinkJustCopied(true);
      copyFeedbackTimeoutRef.current = setTimeout(() => {
        setLinkJustCopied(false);
        copyFeedbackTimeoutRef.current = null;
      }, 2000);
      toast.success("Link copied to clipboard");
    } catch (error) {
      console.error("Failed to copy share link", error);
      toast.error("Could not copy link. Please try again.");
    }
  }, [shareLink]);

  useEffect(
    () => () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
    },
    [],
  );

  const handleShare = async () => {
    const normalizedEmail = shareEmail.trim();

    if (!validateEmail(normalizedEmail)) {
      toast.error("Invalid email address");
      return;
    }

    const request: ShareSubmissionRequest = {
      share_email: normalizedEmail,
    };

    setIsSharing(true);
    try {
      await postSubmissionShare({
        path: { submission_id: submissionId ?? "" },
        body: request,
      });
      toast.success(`${appTitle} shared successfully`);
      setShareEmail("");
      onShared?.();
    } catch (error) {
      console.error("Error while sharing report", error);
      toast.error("Failed to share report. Please try again.");
    } finally {
      setIsSharing(false);
    }
  };

  const normalizedEmail = shareEmail.trim();
  const isEmailValid = !normalizedEmail || validateEmail(normalizedEmail);
  const showEmailError = hasInteractedWithEmail && !isEmailValid;
  const canShare = !!normalizedEmail && isEmailValid && !isSharing;
  const canTransfer = isDevMode && !!selectedTransferAgency;

  const selectedTransferOption = useMemo(
    () =>
      transferAgencyOptions.find((o) => o.id === selectedTransferAgency) ??
      null,
    [transferAgencyOptions, selectedTransferAgency],
  );

  const handleTransfer = () => {
    if (!selectedTransferAgency) {
      return;
    }

    const targetName = selectedTransferOption?.name ?? selectedTransferAgency;

    toast.info(
      `Transfer preview only — no agency move was performed. Selected: ${targetName}.`,
    );
  };

  const onEmailKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      void handleShare();
    }
  };

  return (
    <Box className={styles.formLayout} sx={!framed ? { pt: 0.5 } : undefined}>
      <Box className={styles.section}>
        <Typography variant="body2" className={styles.fieldLabel}>
          Email address
        </Typography>
        <Typography
          variant="body2"
          className={styles.helperText}
          sx={{ maxWidth: "100%" }}
        >
          {`We'll email a secure link the recipient can use to open this ${appTitle.toLowerCase()}.`}
        </Typography>
        <FormControl fullWidth>
          <TextField
            id="share-report-email"
            fullWidth
            variant="outlined"
            label=""
            hiddenLabel
            placeholder="name@example.com"
            value={shareEmail}
            error={showEmailError}
            helperText={showEmailError ? "Enter a valid email address." : ""}
            onBlur={() => setHasInteractedWithEmail(true)}
            onKeyDown={onEmailKeyDown}
            onChange={(e) => {
              if (!hasInteractedWithEmail) setHasInteractedWithEmail(true);
              setShareEmail(e.target.value);
            }}
            inputProps={{
              "aria-invalid": showEmailError,
              "aria-label": "Email address",
            }}
            sx={{
              "& .MuiInputBase-input::placeholder": {
                color: "#999",
                opacity: 1,
              },
              "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
            }}
          />
        </FormControl>
        <LoadingButton
          fullWidth
          onClick={() => void handleShare()}
          color="info"
          variant="contained"
          disableElevation
          loading={isSharing}
          disabled={!canShare}
          className={styles.sendLinkButton}
        >
          Send link
        </LoadingButton>
      </Box>

      <Box className={styles.section}>
        <Typography variant="body2" className={styles.fieldLabel}>
          Share link
        </Typography>
        <Typography variant="body2" className={styles.helperText}>
          Copy a direct link to the condensed {appTitle.toLowerCase()} view.
        </Typography>
        <FormControl fullWidth>
          <TextField
            id="share-report-link"
            fullWidth
            variant="outlined"
            className={styles.linkReadonly}
            value={shareLink}
            inputProps={{ readOnly: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ ml: 0.5, pl: 0, maxHeight: 40 }}
                >
                  <Tooltip
                    title={linkJustCopied ? "Copied" : "Copy to clipboard"}
                  >
                    <span>
                      <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        color={linkJustCopied ? "success" : "primary"}
                        onClick={() => void handleCopyLink()}
                        className={styles.copyActionButton}
                        disableElevation
                        startIcon={
                          linkJustCopied ? (
                            <CheckRounded fontSize="inherit" />
                          ) : (
                            <ContentCopyRounded fontSize="inherit" />
                          )
                        }
                        aria-label="Copy public link to clipboard"
                      >
                        {linkJustCopied ? "Copied" : "Copy"}
                      </Button>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            size="small"
            title={shareLink}
          />
        </FormControl>
      </Box>

      <Box className={styles.section}>
        <Typography
          variant="body2"
          className={styles.fieldLabel}
          sx={disabledTransferTextColor}
        >
          Transfer to another agency
        </Typography>
        <Typography
          variant="body2"
          className={styles.helperText}
          sx={disabledTransferTextColor}
        >
          Move this {appTitle.toLowerCase()} to a different service. Preview
          only.
        </Typography>
        {!isDevMode && (
          <Typography
            variant="body2"
            className={styles.helperText}
            sx={disabledTransferTextColor}
          >
            Contact{" "}
            <Link href="mailto:support@tryrubicon.com" color="inherit">
              support@tryrubicon.com
            </Link>{" "}
            to enable.
          </Typography>
        )}
        <Box
          className={styles.transferBlock}
          sx={
            !isDevMode
              ? {
                  opacity: 0.5,
                  filter: "grayscale(25%)",
                  pointerEvents: "none",
                }
              : undefined
          }
        >
          <p className={styles.transferWarning}>
            <WarningAmberOutlined className={styles.transferWarningIcon} />
            <span>
              <strong>Warning:</strong> Transferring this report will remove it
              from your current agency&rsquo;s list.
            </span>
          </p>
          <FormControl fullWidth>
            <Autocomplete
              id="transfer-agency-select"
              options={transferAgencyOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedTransferOption}
              onChange={(_, newValue) => {
                setSelectedTransferAgency(newValue?.id ?? "");
              }}
              disabled={!isDevMode || transferAgencyOptions.length === 0}
              noOptionsText="No other agencies available"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target agency"
                  placeholder="Select agency…"
                  variant="outlined"
                />
              )}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
              }}
            />
          </FormControl>
          <Button
            fullWidth
            variant="contained"
            color="info"
            disabled={!canTransfer}
            onClick={handleTransfer}
            className={styles.transferActionButton}
            disableElevation
          >
            Transfer
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ShareReportContent;
